import { CalendarRange } from 'lucide-react';
import { useRecoilValue } from 'recoil';

import { Button } from '@/components/button';
import { Heading } from '@/components/heading';
import { CarrierLogoStack } from '@/components/modules/carrier/logo-stack';
import { PortCode } from '@/components/port-code';
import { Text } from '@/components/text';
import { MODE_AIR } from '@/lib/constants';
import { getLocationNameWithCode } from '@/lib/models/location/get-location-name-with-code';
import { getLocationByIdState, getLocationsByIdState } from '@/state/locations';
import { SegmentModeType } from '@/types/api-types';
import { getPreferredIdentifier } from '@/utils/helpers';
import { createScheduleSearchPageUrl } from '@/utils/helpers/page-urls';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

export interface JourneyHeaderProps {
  originId: string;
  destinationId: string;
  mode: SegmentModeType;
  transferLocationIds?: string[];
  carrierIds?: string[];
  shouldShowSchedulesLink?: boolean;
}

const JourneyHeader = ({
  originId,
  destinationId,
  transferLocationIds = [],
  mode,
  carrierIds,
  shouldShowSchedulesLink = false,
}: JourneyHeaderProps) => {
  const origin = useRecoilValue(getLocationByIdState(originId));
  const destination = useRecoilValue(getLocationByIdState(destinationId));
  const originIdentifier = getPreferredIdentifier(origin.identifiers);
  const destinationIdentifier = getPreferredIdentifier(destination.identifiers);
  const transferLocations = useRecoilValue(getLocationsByIdState(transferLocationIds));

  const { name: fromName } = origin;
  const { name: toName } = destination;
  const viaString = transferLocations?.length
    ? transferLocations
        .map((location) => {
          const { name, code } = getLocationNameWithCode(location);
          return `${code} (${name})`;
        })
        .join(' & ')
    : null;

  const getScheduleUrlParams = () => {
    const params = {
      origin,
      destination,
      mode,
    };
    if (mode === MODE_AIR && carrierIds !== undefined) {
      return { ...params, filters: { carrierIds } };
    }

    return params;
  };

  const handleClickSchedulesLink = () => {
    trackUser.event('Go to: Schedules', {
      location: 'Route Overview',
      mode,
      portPair: `${originIdentifier?.value} - ${destinationIdentifier?.value}`,
    });
  };

  const { heading, headingText, subHeadingText, locationsWrapper, arrow, content } = styles();

  return (
    <div>
      <div className={heading()}>
        <div>
          <Heading variant="h2" className={headingText()}>
            {fromName} to <span>{toName}</span>
          </Heading>
          {viaString && (
            <Text variant="secondary" className={subHeadingText()}>
              Via {viaString}
            </Text>
          )}
        </div>
      </div>
      <div className="flex justify-between">
        <div className={content()}>
          {carrierIds && Boolean(carrierIds?.length) && (
            <CarrierLogoStack carrierIds={carrierIds} mode={mode} variant="md" />
          )}
          {originIdentifier && destinationIdentifier && (
            <div className={locationsWrapper()}>
              <PortCode port={origin} mode={mode} />
              <span className={arrow()}>→</span>
              <PortCode port={destination} mode={mode} isDestination />
            </div>
          )}
          {shouldShowSchedulesLink && (
            <div className="-my-2 flex items-center rounded-lg bg-blue-50 px-4 py-2">
              <div>
                <Text variant="sm" className="mb-1 italic text-grey-600">
                  See next scheduled departures
                </Text>
                <div className="flex items-center">
                  <CalendarRange size="20" className="mr-2" />
                  <Button
                    href={createScheduleSearchPageUrl(getScheduleUrlParams())}
                    onClick={handleClickSchedulesLink}
                    variant="link"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium"
                  >
                    Go to Schedules
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    arrow: 'mx-4 mb-1',
    content: 'flex items-center gap-6',
    heading: 'mb-4 flex justify-between',
    headingText: 'mb-0 max-w-[760px] !text-md leading-tight',
    locationsWrapper: 'flex items-end text-base',
    subHeadingText: 'mb-0 mt-1 text-grey-600',
  },
});

export { JourneyHeader };
